import React from 'react'
import PropTypes from 'prop-types'
import ColorStopsHolder from './ColorStopsHolder'
import Palette from './Palette'
import ColorPicker from '@/components/ColorPicker'

import styles from './styles.scss'

const HALF_STOP_WIDTH = 5

const convertToCss = (data, type = 'linear') => {
  if (type === 'linear') {
    return `linear-gradient(to right, ${data
      .map(i => `${i.color} ${i.pos * 100}%`)
      .join(',')})`
  } else {
    return `radial-gradient(${data
      .map(i => `${i.color} ${i.pos * 100}%`)
      .join(',')})`
  }
}

const toState = palette => ({
  palette: palette.map((c, i) => ({ id: i + 1, ...c })),
  activeId: 1,
  pointX: null,
  type: 'linear'
})

const fromState = palette => {
  const compare = ({ pos: pos1 }, { pos: pos2 }) => pos1 - pos2
  const sortedPalette = palette.sort(compare)
  return sortedPalette.map(({ pos, color }) => ({
    pos: pos.toPrecision(3),
    color
  }))
}

class GradientBuilder extends React.Component {
  constructor(props) {
    super(props)
    this.state = { ...toState(props.palette) }
    this.handlePosChange = this.handlePosChange.bind(this)
    this.handleAddColor = this.handleAddColor.bind(this)
    this.handleActivate = this.handleActivate.bind(this)
    this.handleDeleteColor = this.handleDeleteColor.bind(this)
    this.handleSelectColor = this.handleSelectColor.bind(this)
    this.onChangeType = this.onChangeType.bind(this)
  }

  get width1() {
    return this.props.width + 1
  }

  get nextId() {
    return Math.max(...this.state.palette.map(c => c.id)) + 1
  }

  get activeStop() {
    return this.state.palette.find(s => s.id === this.state.activeId)
  }

  get mapStateToStops() {
    const activeId = this.state.activeId
    const pointX = this.state.pointX
    return this.state.palette.map(c => ({
      ...c,
      pos: this.width1 * c.pos - HALF_STOP_WIDTH,
      isActive: c.id === activeId,
      pointX
    }))
  }

  get colorPicker() {
    const { children } = this.props
    const props = {
      color: this.activeStop.color,
      onChange: this.handleSelectColor,
    }
    if (!children) {
      return <ColorPicker {...props} />
    }
    const child = React.Children.only(children)
    return React.cloneElement(child, props)
  }

  notifyChange(palette) {
    const data = fromState(palette)
    const type = this.state.gradientType || 'linear'
    if (this.props.onPaletteChange) {
      this.props.onPaletteChange(data)
    }
    if (this.props.onGradientChange) {
      this.props.onGradientChange(convertToCss(data, type))
    }
  }

  handleActivate(activeId) {
    this.setState({ activeId })
  }

  handleDeleteColor(id) {
    if (this.state.palette.length < 3) return
    const palette = this.state.palette.filter(c => c.id !== id)
    const activeId = palette.reduce(
      (a, x) => (x.pos < a.pos ? x : a),
      palette[0]
    ).id
    this.setState({ palette, activeId })
    this.notifyChange(palette)
  }

  handlePosChange({ id, pos }) {
    const palette = this.state.palette.map(c =>
      id === c.id
        ? { ...c, pos: (pos + HALF_STOP_WIDTH) / this.width1 }
        : { ...c }
    )
    this.setState({ palette })
    this.notifyChange(palette)
  }

  handleAddColor({ pos, pointX }) {
    const color = this.activeStop.color
    const entry = { id: this.nextId, pos: pos / this.width1, color }
    const palette = [...this.state.palette, entry]
    this.setState({ palette, pointX })
    this.notifyChange(palette)
  }

  handleSelectColor(color) {
    let { palette, activeId } = this.state
    color = color.hex
    palette = palette.map(c => (activeId === c.id ? { ...c, color } : { ...c }))
    this.setState({ palette })
    this.notifyChange(palette)
  }

  componentWillReceiveProps({ palette: next }) {
    const { palette: current } = this.props
    const length = Math.min(next.length, current.length)
    for (let i = 0; i < length; i++) {
      if (
        next[i].pos !== current[i].pos ||
        next[i].color !== current[i].color
      ) {
        this.setState({ ...toState(next) })
        return
      }
    }
  }

  onChangeType(type) {
    this.setState({ gradientType: type }, () =>
      this.notifyChange(this.state.palette)
    )
  }

  render() {
    const { drop, backgroundGradient } = this.props
    const min = 0
    const max = 249

    const radial = backgroundGradient && backgroundGradient.indexOf('radial') !== - 1
    const linear = backgroundGradient && backgroundGradient.indexOf('linear') !== - 1

    return (
      <div className={styles['wrapper-gradient-builder']}>
        <div>{this.colorPicker}</div>
        <div className='ba-mg-t-1'>
          <Palette palette={this.state.palette} />
          <ColorStopsHolder
            stops={this.mapStateToStops}
            limits={{ min, max, drop }}
            onPosChange={this.handlePosChange}
            onAddColor={this.handleAddColor}
            onActivate={this.handleActivate}
            onDeleteColor={this.handleDeleteColor}
          />
        </div>
        <div>
          <div>
            <input type="radio"
              id="linear"
              className="radio-button"
              name="gradient"
              checked={linear}
              onChange={() => this.onChangeType('linear')}
            />
            <label htmlFor="linear" className={linear ? 'ba-color-blue1' : ''}>
              Linear
            </label>
          </div>

          <div>
            <input type="radio"
              id="radial"
              className="radio-button"
              name="gradient"
              checked={radial}
              onChange={() => this.onChangeType('radial')}
            />
            <label htmlFor="radial" className={radial ? 'ba-color-blue1' : ''}>
              Radial
            </label>
          </div>
        </div>
      </div>
    )
  }
}

GradientBuilder.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  drop: PropTypes.number,
  palette: PropTypes.arrayOf(
    PropTypes.shape({
      pos: PropTypes.number,
      color: PropTypes.string
    }).isRequired
  ),
  onPaletteChange: PropTypes.func,
  onGradientChange: PropTypes.func
}

GradientBuilder.defaultProps = {
  width: 202,
  height: 16,
  drop: 50,
  palette: [{ pos: 0, color: '#9adafa' }, { pos: 1, color: '#028080' }]
}

export default GradientBuilder



// WEBPACK FOOTER //
// ./src/components/gradientBuilder/GradientBuilder.js